<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"

import { BookingStatusBadge } from "@/shared/ui"
import type { Booking } from "@/entities/booking"
import { bookingTop, bookingHeight, minutesOfDayInTz } from "../model/timeGrid"
import { useRecordsStore } from "../model/store"

interface Props {
  booking: Booking
  gridStartMin: number
  tz: string
  catSlot: number
  serviceName?: string
}

const props = defineProps<Props>()
const store = useRecordsStore()
const { t } = useI18n()

const BADGE_HEIGHT_THRESHOLD = 72

const topPx = computed(() => bookingTop(props.booking, props.gridStartMin, props.tz))
const heightPx = computed(() => bookingHeight(props.booking, props.tz))

const showBadge = computed(
  () => heightPx.value >= BADGE_HEIGHT_THRESHOLD && props.booking.status !== "cancelled",
)

const statusLabel = computed(() => {
  const key = `journal.status.${props.booking.status}` as const
  return t(key as string)
})

/** Format a UTC ISO instant as HH:MM in the given tz. */
function formatHHMM(iso: string, tz: string): string {
  const minOfDay = minutesOfDayInTz(iso, tz)
  const h = Math.floor(minOfDay / 60)
  const m = minOfDay % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const timeRange = computed(
  () =>
    `${formatHHMM(props.booking.startsAt, props.tz)}–${formatHHMM(props.booking.endsAt, props.tz)}`,
)

const ariaLabel = computed(() => {
  const parts: string[] = []
  if (props.serviceName) parts.push(props.serviceName)
  parts.push(props.booking.clientName)
  parts.push(timeRange.value)
  parts.push(statusLabel.value)
  return parts.join(", ")
})

function handleClick(): void {
  store.openActions(props.booking)
}
</script>

<template>
  <button
    class="booking-card"
    :class="`is-${booking.status}`"
    :style="{ top: `${topPx}px`, height: `${heightPx}px` }"
    :data-cat="catSlot"
    :aria-label="ariaLabel"
    type="button"
    @click="handleClick"
  >
    <span class="booking-card__service">{{ serviceName ?? "—" }}</span>
    <span class="booking-card__client">{{ booking.clientName }}</span>
    <BookingStatusBadge
      v-if="showBadge"
      :status="booking.status"
      size="sm"
    />
  </button>
</template>

<style scoped>
.booking-card {
  position: absolute;
  left: 2px;
  right: 2px;
  z-index: 2;
  border-radius: 4px;
  padding: 4px 6px;
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  border: none;
  background-color: var(--muted);
  color: var(--foreground);
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 1px;
  }
}

/* Category palette bindings */
.booking-card[data-cat="0"] { background-color: var(--cat-0-bg, var(--muted)); border-left: 3px solid var(--cat-0-accent, var(--primary)); color: var(--cat-0-fg, var(--foreground)); }
.booking-card[data-cat="1"] { background-color: var(--cat-1-bg, var(--muted)); border-left: 3px solid var(--cat-1-accent, var(--primary)); color: var(--cat-1-fg, var(--foreground)); }
.booking-card[data-cat="2"] { background-color: var(--cat-2-bg, var(--muted)); border-left: 3px solid var(--cat-2-accent, var(--primary)); color: var(--cat-2-fg, var(--foreground)); }
.booking-card[data-cat="3"] { background-color: var(--cat-3-bg, var(--muted)); border-left: 3px solid var(--cat-3-accent, var(--primary)); color: var(--cat-3-fg, var(--foreground)); }
.booking-card[data-cat="4"] { background-color: var(--cat-4-bg, var(--muted)); border-left: 3px solid var(--cat-4-accent, var(--primary)); color: var(--cat-4-fg, var(--foreground)); }
.booking-card[data-cat="5"] { background-color: var(--cat-5-bg, var(--muted)); border-left: 3px solid var(--cat-5-accent, var(--primary)); color: var(--cat-5-fg, var(--foreground)); }
.booking-card[data-cat="6"] { background-color: var(--cat-6-bg, var(--muted)); border-left: 3px solid var(--cat-6-accent, var(--primary)); color: var(--cat-6-fg, var(--foreground)); }
.booking-card[data-cat="7"] { background-color: var(--cat-7-bg, var(--muted)); border-left: 3px solid var(--cat-7-accent, var(--primary)); color: var(--cat-7-fg, var(--foreground)); }
.booking-card[data-cat="8"] { background-color: var(--cat-8-bg, var(--muted)); border-left: 3px solid var(--cat-8-accent, var(--primary)); color: var(--cat-8-fg, var(--foreground)); }
.booking-card[data-cat="9"] { background-color: var(--cat-9-bg, var(--muted)); border-left: 3px solid var(--cat-9-accent, var(--primary)); color: var(--cat-9-fg, var(--foreground)); }

.booking-card.is-completed { opacity: 0.6; }
.booking-card.is-cancelled { opacity: 0.4; }
.booking-card.is-cancelled .booking-card__service,
.booking-card.is-cancelled .booking-card__client { text-decoration: line-through; }

.booking-card__service {
  font-size: 0.75rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.booking-card__client {
  font-size: 0.7rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
